import { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { getDrawMode } from 'utils/map';
import MapEditor from './MapEditor';
import { Georeference, GeoreferenceCoordinate } from 'apollo/schema/types';
import {
  GeoreferenceFormValues,
  GeoreferenceFormValuesCoordinate,
} from 'utils/modules/georeference';

/** Formats string-coordinates into the {lat,lng} shape google maps expects. */
function formatCoordinates(latLngStringPairs: GeoreferenceCoordinate[]) {
  return latLngStringPairs.map(pair => ({
    lat: parseFloat(pair.latitude),
    lng: parseFloat(pair.longitude),
  }));
}

/**
 * Formats saved georeferences for rendering on the map. The in-progress
 * drawing is no longer merged in here: TerraDraw renders it live while drawing.
 */
function formatGeoreferencesForMap(georefs: Georeference[]) {
  return georefs
    .filter(el => el.data.coordinates)
    .map(el => ({
      ...el,
      data: {
        coordinates: formatCoordinates(el.data.coordinates),
      },
    }));
}

type Props = {
  georeferences: Georeference[];
};

function MapEditorContainer({ georeferences }: Props) {
  const { values, setFieldValue } = useFormikContext<GeoreferenceFormValues>();
  const { dataType } = values;

  useEffect(() => {
    // When data type changes, remove any coordinates that have been set
    setFieldValue('data', null);
  }, [dataType, setFieldValue]);

  function handleDrawingComplete(
    coordinates: GeoreferenceFormValuesCoordinate[] | null,
  ) {
    if (coordinates && coordinates.length && dataType) {
      setFieldValue('data', { coordinates });
    } else {
      setFieldValue('data', null);
    }
  }

  const mapFormattedGis = formatGeoreferencesForMap(georeferences);

  return (
    <MapEditor
      georeferences={mapFormattedGis}
      drawMode={getDrawMode(values.dataType)}
      onDrawingComplete={handleDrawingComplete}
    />
  );
}

export default MapEditorContainer;
